import type { FeedIssue } from "./feed_issues";
import type { RowBands } from "./signal";

export type FreshnessMeta = { data_as_of: string | null; source: string | null; expected_max_age_hours: number | null };

export type Market = "UK" | "EU" | "AE";
export type FeedEntry = { latest: string | null; stale: boolean };
/** C2 v1.11 adds `kpi` (the daily KPI sheets read); optional until `deploy/api.md` step 14 is live, so an older envelope still types.
 * `pool_cogs` is the weekly Xero stock read (C2 v1.4 `freshness`, stale after its own 8 days: owner ruling Q11); the Price Run
 * names its date (C1 v1.17, owner ruling Q13: no re-read button, the export is the accountant's). */
export type Feeds = { universe: FeedEntry; pcogs: FeedEntry; fulfilment: FeedEntry; fee_registry: FeedEntry; fx: FeedEntry; kpi?: FeedEntry; pool_cogs?: FeedEntry; recharge?: FeedEntry };
/** The two COGS measures C4 v1.4 computes a grid on (CL-019). `projected_cogs` is release R3 and never a grid basis here. */
export type Basis = "last_production_cogs" | "current_inventory_cogs";
export const DEFAULT_BASIS: Basis = "last_production_cogs";
export type Envelope = {
  market: Market; grid_run_id: number; computed_at: string; object_uri: string;
  expected_max_age_hours: number; stale: boolean; feeds: Feeds;
  /** C2 v1.12: the basis this grid's margins are driven by. Optional until `deploy/api.md` step 15 is live; the bases the run
   * computes are the grid's own `bases_available`. */
  basis?: Basis;
  /** C2 v1.18: the Config warn-after in hours (12, 24 or 48) that `stale` and `expected_max_age_hours` follow, plus a two-hour
   * grace; null while it is not set (a day). Absent on an API before `deploy/api.md` step 21, which reads as not set. */
  stale_basis_hours?: number | null;
  /** C2 v1.26 (c4-as-of-grid, `deploy/api.md` step 29): the date asked for (the run date when none was), the date of the set
   * served (the latest change date on or before it, else the run date), the run's change dates within twelve months, the
   * change dates on or before it that the run could not compute for this market, and the date the COGS are read on (the run
   * date: a future set moves fees only until Projected COGS ships). Absent on an older API, which reads as today only. */
  as_of?: string; in_force_from?: string; change_dates?: ChangeDate[]; incomplete_dates?: string[]; cogs_as_of?: string | null;
  /** C2 v1.56 (C1 v1.60, GATE-1089): the open issues of the feeds behind the grid; absent on an older API, read as none. */
  feed_issues?: FeedIssue[];
};
/** One date at which an input changes, and how many of each source change on it (C4 v1.6 `change_dates`). */
export type ChangeDate = { date: string; sources: { fee_rate: number; fulfilment_future: number; projected_cogs: number } };
/** One component that differs on a future set from today's (C4 v1.6 `changes`): both figures as C2 wrote them, the date it is in
 * force from and why (`fee_rate`: the registry version; `fulfilment_future`: PFC's note). A store row names each country's. */
export type RowChange = {
  component: Component; today: string | null; as_of: string | null; effective_from: string; source: "fee_rate" | "fulfilment_future";
  note: string | number | null; country?: string;
};
/** The earliest pending fee-rate change for an Amazon row (C4 v1.5 `upcoming_fee_change`, relative to the run date): a fixed
 * amount in `currency`, or a percentage rate as a fraction. Null while PFC holds none; absent on a store or ship-country row. */
export type UpcomingFee = { component: Component; value: string; rate_type: string; currency: string | null; effective_from: string };
export type Grain = "marketplace" | "store" | "ship_country";
export type Channel = "amazon" | "shopify";
export type Component = "referral" | "dsf" | "fba_per_unit" | "pick_pack" | "packaging" | "courier" | "other";
export type MissingReason =
  | "no_price" | "no_vat_rate" | "no_production_record" | "no_current_inventory_cost" | "no_fx_rate" | "no_referral_cost" | "no_dsf_cost"
  | "no_fba_per_unit_cost" | "no_pick_pack_cost" | "no_packaging_cost" | "no_courier_cost" | "no_other_cost" | "other_currency_mismatch" | "no_fulfilment_rows";
/** C3 `component_flag` vocabulary, all fifteen values. */
export type Flag =
  | "BILLED" | "ESTIMATE" | "PROJECTED" | "STALE" | "NO SALES" | "THIN" | "CORRECTED" | "NO FORWARD PRICE" | "OVERRIDE"
  | "INCOMPLETE" | "OFFER EXPIRED" | "AMBIGUOUS SELECTION" | "NOT COMPUTED" | "COGS AFTER ORDER" | "MISSING";
export type Price = { value: string; currency: string; set_at: string; source: string };
export type Cogs = {
  value: string; currency: string; native_value: string; native_currency: string;
  fx_rate: string | null; fx_date: string | null; fx_source: string | null; effective_date: string; flag: Flag;
};
/** C4 v1.13 (c4-cogs-detail), on `cogs_measures` only, never on `cogs`: a Current Inventory measure with a value carries the
 * units its Xero read held (a decimal string: "1317", "2.5") and the entity holding them (`ENTITY_LABEL`'s codes); a Last
 * Production measure with a value carries how far the production cost sits from the inventory cost, in percent to one decimal
 * ("-11.1", "0.0", "3.0"). Absent from an older grid and wherever C4 does not know them. */
export type MeasureDetail = { quantity_available?: string; entity?: string; direction_pct?: string };
/** One COGS measure as C4 v1.4 carries it beside the driving `cogs`: the figure, or why it is missing (`no_currency` when the
 * row has no price or fee currency to convert into). Both measures ride every row and are never combined (INV-1). */
export type CogsMeasure = (Cogs & MeasureDetail) | { missing: MissingReason | "no_currency" };
export type CogsMeasures = Record<Basis, CogsMeasure>;
export type FulfilmentComponent = { component: Component; value: string | null; flag: Flag; order_count?: number | null; fed_on?: string };
export type Fulfilment = { total: string | null; components: FulfilmentComponent[] };
export type Margins = {
  gross_pct: string | null; fulfilment_pct: string | null; cogs_fulfilment_pct: string | null; fees_pct: string | null;
  true_contribution_pct: string | null; true_contribution_missing: string[];
  /** The same figure without ad spend (PP-01.3); absent from a grid computed before it. */
  true_contribution_ex_ads_pct?: string | null; true_contribution_ex_ads_missing?: string[];
};
export type Weighting = { method: "orders"; label: string; countries_counted: number; countries_total: number; orders_counted: number; orders_total: number };
/** C2 v1.79 (c2-grid-competition, bug b85a3efc): the row's price against the competitor max at its committed price, or why not. */
export type CompetitorMax = { value: string | null; reference: string | null; observed_at: string | null; competitor_count: number | null; reason: string | null };
export type RowCompetition = { vs_competitor_max_pct: CompetitorMax };
export type GridRow = {
  xero_sku: string; product: string; variant: string; channel: Channel; channel_location: string;
  /** Null on a marketplace row whose marketplace is missing from C4's reference table. */
  location: string | null; strategy: string | null; grain: Grain; marketplace_id: string; marketplace_name: string | null;
  parent_sku?: string; orders?: number;
  price: Price | null; currency: string | null; vat_rate: string | null; net_price: string | null;
  cogs: Cogs | null; fulfilment: Fulfilment | null; margins: Margins; weighting?: Weighting; missing: MissingReason[];
  /** C4 v1.4: absent on a grid computed before it. */
  cogs_measures?: CogsMeasures;
  /** C4 v1.6: on a future set only, what differs from today's set (empty when nothing does). */
  changes?: RowChange[];
  /** C4 v1.5: on an Amazon row only. */
  upcoming_fee_change?: UpcomingFee | null;
  /** C2 per-row bands (c1-price-run-signal-rows, CLB-022): each margin and lens cell's band from the KPI system's registered ranges.
   * Absent on an older API: every cell paints neutral, never a guessed band. */
  bands?: RowBands;
  /** C2 v1.79: the competitor figure the Competition cell shows. Absent on an older API: the cell reads no data and says so. */
  competition?: RowCompetition;
};
export type Grid = {
  market: Market; basis: string; bases_available: string[]; as_of: string; prices_imported_at: string | null;
  fx: { source: string; rate_date: string | null; rates: Record<string, string> }; row_count: number; rows: GridRow[];
  /** C4 v1.6, a future set only: its date and the count of rows (never a store) whose figures differ from today's. */
  in_force_from?: string; changed_rows?: number;
};
export type GridResponse = { envelope: Envelope; grid: Grid };

const BASE = import.meta.env.VITE_PRICING_API_BASE ?? "/precision_pricing/api";
const HEADERS = { Accept: "application/json", "Content-Type": "application/json" };
/** Five minutes: how long a loaded grid is reused before a tab switch or refocus refetches it. */
export const GRID_STALE_TIME = 300_000;

/** The RRP facts C2 v1.8 sends on a stage refusal (`below_rrp`, `rrp_not_comparable`) and on each line of `below_rrp_at_commit`. */
export type RrpFacts = {
  rrp?: string; rrp_currency?: string; anchor_market?: string; comparable?: boolean; fx_rate?: string | null;
  fx_rate_date?: string | null; fx_stale?: boolean | null; compared_price?: string | null; deviation_pct?: string | null;
};
/** One line a commit refused, as C2 lists it in `error.lines` (`below_floor_at_commit`, `unchanged_at_commit`, v1.8 `below_rrp_at_commit`). */
export type RefusedLine = RrpFacts & {
  line_id: number; xero_sku: string; marketplace_id: string; new_price: string; currency: string;
  reason?: string; floor?: string; floor_currency?: string; committed_price?: string; rrp_exception_until?: string;
};
/** The facts C2 merges into an error body beside `reason` and `message` (the floor or the RRP on a stage refusal, the lines on a
 * commit refusal, the line ids on `rrp_changed_at_commit`). */
export type ErrorFacts = RrpFacts & {
  reason?: string; message?: string; floor?: string; floor_currency?: string; lines?: RefusedLine[]; line_ids?: number[];
};
type ApiErrorBody = { error?: ErrorFacts | string; detail?: string };
export class PricingError extends Error {
  constructor(readonly status: number, readonly body: ApiErrorBody | null) {
    super(`Pricing API ${status}`);
  }
  /** The API's `error.reason`, or the proxy's `error` string, or null. */
  reason(): string | null {
    const error = this.body?.error;
    if (typeof error === "string") return error;
    return error?.reason ?? null;
  }
  /** The API's error facts; empty for a proxy error or a body that could not be read. */
  facts(): ErrorFacts {
    const error = this.body?.error;
    return error && typeof error === "object" ? error : {};
  }
}

async function safeBody(response: Response): Promise<ApiErrorBody | null> {
  try { return await response.json() as ApiErrorBody; } catch { return null; }
}

export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // cache: "no-store" (owner ruling A1, 2026-09-18). The envelope's `stale` and `feeds` are computed per request,
  // but C2's ETag changes only with the run id, so a revalidated 304 would replay yesterday's flags.
  const response = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include", cache: "no-store", headers: HEADERS, ...init });
  if (!response.ok) throw new PricingError(response.status, await safeBody(response));
  return await response.json() as T;
}

/** A text answer, such as C2 v1.46's CSV export: the same request as `call`, the body read as text, and the attachment name the
 * answer's `Content-Disposition` carries (null when it carries none). A refusal is a `PricingError` as from `call`. */
export async function callText(path: string): Promise<{ text: string; name: string | null }> {
  const response = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include", cache: "no-store", headers: { Accept: "text/csv, application/json" } });
  if (!response.ok) throw new PricingError(response.status, await safeBody(response));
  const name = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? null;
  return { text: await response.text(), name };
}

/** The grid of one market on one basis (C2 v1.12 `?basis=`), on a picked date when `asOf` is set (C2 v1.26 `?as_of=`). The
 * default basis on today asks with no query string, exactly as before, so an older API answers the same bytes; another basis
 * or a date is its own resource and its own cache entry. */
export const getGrid = (market: Market, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) => {
  const query = [basis === DEFAULT_BASIS ? null : `basis=${basis}`, asOf === null ? null : `as_of=${asOf}`].filter(Boolean).join("&");
  return call<GridResponse>(`/grid/${market}${query ? `?${query}` : ""}`);
};

/** The one grid query per market, basis and date, shared by the shell's freshness hook (default basis, today) and the tab so both
 * read one cache entry. The default basis keeps the R1b key and a picked date adds two parts, so every seeded cache and every
 * other tab is untouched. */
export function gridQuery(market: Market, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) {
  const base = basis === DEFAULT_BASIS ? ["pricing", "grid", market] : ["pricing", "grid", market, basis];
  const queryKey = asOf === null ? base : [...base, "as_of", asOf];
  return { queryKey, queryFn: () => getGrid(market, basis, asOf), retry: 1, staleTime: GRID_STALE_TIME };
}

/** C2's refusal of a date outside today to twelve months ahead, or not a date (C2 v1.26 `bad_as_of`). */
export function isBadAsOf(error: unknown): boolean {
  return error instanceof PricingError && error.status === 422 && error.reason() === "bad_as_of";
}

export function isNoGrid(error: unknown): boolean {
  return error instanceof PricingError && error.status === 404 && error.reason() === "no_grid_yet";
}

/** The S8 meta the shell's freshness line reads, built from the C2 envelope (the route does not send {data, meta}). */
export function metaOf(envelope: Envelope): FreshnessMeta {
  return {
    data_as_of: envelope.computed_at,
    source: `Precision Pricing · pricing-api · run ${envelope.grid_run_id}`,
    expected_max_age_hours: envelope.expected_max_age_hours,
  };
}

// Price Run stage and commit (C2 v1.3, release R4 minus C6). People only: reads need the area view, writes the module edit.

export type LineState = "saved" | "committed" | "failed";
/** One staged or committed price, exactly as C2 returns it. Money is a decimal string; the browser never computes with it. */
export type PriceRunLine = {
  id: number; run_id: number; market: Market; xero_sku: string; product: string; variant: string; channel: Channel;
  channel_location: string; marketplace_id: string; marketplace_name: string | null; amazon_sku: string | null;
  old_price: string | null; new_price: string; currency: string; state: LineState; floor_override_reason: string | null;
  rrp_exception_reason: string | null; rrp_exception_until: string | null; fee_reason: string | null;
  // C1 v1.50: the temporary price's pair (C2 v1.52); absent from an API older than v1.52, so read as null.
  override_reason?: string | null; override_until?: string | null;
  staged_by: string; saved_at: string; committed_at: string | null;
  /** C2 v1.71 (c2-run-propose R12): the proposal record of a line the strategy run saved; a person's edit keeps it. Null on a
   * typed line; absent on an API before v1.71, read as null. */
  proposal?: LineProposal | null;
};
/** C2 v1.76 (c2-proposal-check, DEC-1403 P9-COPY-2 behaviour 5): the rule check saved with a proposal, judged at the proposed price. It stays
 * fixed when a person moves the line. `rules_met_at` + `not_met` = `checked`; `unverified` is separate; `dropped` is detail and overlaps `not_met`. */
export type ProposalNotMet = { priority: number; rule_id: number; metric: string; label: string; text: string; value: string | null; currency: string | null };
export type ProposalDropped = { priority: number; rule_id: number; metric: string; label: string; text: string; reason: string };
export type ProposalCheck = {
  checked: number[]; not_met: ProposalNotMet[]; dropped: ProposalDropped[]; unverified: UnverifiedRule[];
  goal_price: string | null; window: { low: string; high: string | null } | null; ending: ProposeEnding | null;
};
/** The check is absent on an answer from before C2 v1.76. */
export type LineProposal = { price: string; method: string; rules_met_at: number[] } & Partial<ProposalCheck>;
export type PriceRun = {
  id: number; market: Market; state: "open" | "committed"; opened_at: string; opened_by: string;
  committed_at: string | null; committed_by: string | null; lines: PriceRunLine[];
  /** C2 v1.56: on a one-run read, the open issues of the feeds behind its lines; absent on an older API. */
  feed_issues?: FeedIssue[];
};
export type LastCommitted = { market: Market; run_id: number; committed_at: string; committed_by: string; line_count: number };
/** `feed_issues` (C2 v1.56): the open issues of the feeds behind every line; absent on an older API. */
export type PriceRuns = { runs: PriceRun[]; last_committed: LastCommitted[]; feed_issues?: FeedIssue[] };
export type StageBody = {
  xero_sku: string; marketplace_id?: string; new_price: string; floor_override_reason?: string;
  rrp_exception_reason?: string; rrp_exception_until?: string; fee_reason?: string; override_reason?: string; override_until?: string;
};
/** One RRP the commit wrote (C2 v1.43 `rrps`): the product's RRP now follows the price committed on its market's anchor
 * marketplace. `anchor_market` is the marketplace id; `previous_rrp` is null for a first RRP. Money is C2's string. */
export type RrpWritten = {
  tracking: string; variant: string; market: Market; rrp: string; currency: string; anchor_market: string;
  previous_rrp: string | null; line_id: number; xero_sku: string;
};
/** `rrps` is absent from an API older than C2 v1.43; the bar reads it as an empty list (C1 v1.42). */
export type CommitResponse = { committed: PriceRun[]; rrps?: RrpWritten[] };

export const getPriceRuns = () => call<PriceRuns>("/price-run");
export const getPriceRun = (runId: number) => call<PriceRun>(`/price-run/${runId}`);
export const stagePrice = (body: StageBody) => call<PriceRunLine>("/price-run/lines", { method: "POST", body: JSON.stringify(body) });
export const unstagePrice = (lineId: number) => call<{ deleted: number }>(`/price-run/lines/${lineId}`, { method: "DELETE" });
/** Commits the named open runs only (owner ruling A2, 2026-09-22: the screen commits one market at a time, by run id), with the
 * lines the review listed (C1 v1.38, C2 v1.41 `expected`): C2 refuses `changed_since_review` and commits nothing if they changed. */
export const commitRuns = (runIds: number[], expected: ReviewedLine[]) =>
  call<CommitResponse>("/price-run/commit", { method: "POST", body: JSON.stringify({ run_ids: runIds, expected }) });
/** One line a commit was reviewed with, as C2 v1.41 reads `expected`. */
export type ReviewedLine = { line_id: number; saved_at: string };
/** The list a commit sends: each listed line's id and its `saved_at` exactly as C2 served it. Never through a `Date`: a `Date`
 * keeps milliseconds and the store keeps microseconds, so C2 would read every line as saved again. */
export const reviewed = (lines: Array<{ id: number; saved_at: string }>): ReviewedLine[] =>
  lines.map((line) => ({ line_id: line.id, saved_at: line.saved_at }));
/** What changed since the review, as C2 v1.41 names it on a 409 `changed_since_review`: the lines saved since and saved again
 * (each as the run read serves it) and the ids no longer saved. Null for any other answer. */
export type ReviewChange<L> = { added: L[]; removed: number[]; resaved: L[] };
export function reviewChange<L>(error: unknown): ReviewChange<L> | null {
  if (!(error instanceof PricingError) || error.status !== 409 || error.reason() !== "changed_since_review") return null;
  const facts = error.facts() as { added?: unknown; removed?: unknown; resaved?: unknown };
  const list = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
  return { added: list<L>(facts.added), removed: list<number>(facts.removed), resaved: list<L>(facts.resaved) };
}

// The strategy price run (C2 v1.71, c2-run-propose): for one market and channel, every strategy row's suggested price saved as a
// line of the open run in one pass, or every check run and nothing saved (`dry_run`). People with edit only (R8).

export type ProposeChannel = "all" | "amazon" | "shopify";
export type TickedRow = { xero_sku: string; marketplace_id: string };
export type ProposeBody = {
  market: Market; basis?: Basis; dry_run?: boolean; replace_saved?: boolean; channel?: ProposeChannel; skus?: TickedRow[];
};
/** A rule the strategy could not check: it read no data (R10, INV-14). It never blocks a proposal. */
export type UnverifiedRule = { priority: number; rule_id: number; metric: string; label: string; reason: string | null };
/** The price-ending step (R11): `applied` false keeps the raw price, and `reason` says why. */
export type ProposeEnding = {
  applied: boolean; direction: "up" | "down" | null; ending: number | null; source: "config" | "strategy" | null; raw: string | null; reason: string | null;
};
export type ProposedPrice = {
  value: string; currency: string; source: "goal" | "window_low" | "window_high" | null; goal_price: string | null;
  window: { low: string; high: string | null } | null; dropped: number[]; verified: boolean | null; rules_met_at: number[] | null;
};
export type ProposeSkip = { reason: string; message: string; detail?: ErrorFacts };
export type ProposeRow = {
  xero_sku: string; marketplace_id: string; grain: string; product: string; variant: string; channel: string;
  status: "proposed" | "skipped"; current: { value: string; currency: string } | null; strategy: { strategy_id: number; name: string; marketplace_id: string; inherited: boolean } | null;
  proposed: ProposedPrice | null; unverified: UnverifiedRule[]; ending: ProposeEnding | null; line_id: number | null;
  skip: ProposeSkip | null; note: string | null;
  /** C2 v1.76: the same check a saved line keeps; null when the row has no suggestion, absent before v1.76. */
  check?: ProposalCheck | null;
};
export type ProposeAnswer = {
  market: Market; basis: Basis; dry_run: boolean; replace_saved: boolean; channel: ProposeChannel; ticked_count: number | null; as_of: string;
  strategised: boolean; not_strategised: string | null; run_id: number | null; row_count: number; proposed_count: number; skipped_count: number;
  skipped_by_reason: Record<string, number>; unverified_by_rule: Record<string, number>; rows: ProposeRow[]; method: string;
};
export const proposePrices = (body: ProposeBody) => call<ProposeAnswer>("/price-run/propose", { method: "POST", body: JSON.stringify(body) });

export const PRICE_RUN_KEY = ["pricing", "price-run"] as const;
/** The open runs and the newest committed run per market. Never stale: a save on another tab must show on the next focus. */
export function priceRunsQuery() {
  return { queryKey: PRICE_RUN_KEY, queryFn: getPriceRuns, retry: 1, staleTime: 0 };
}
/** One run with its lines; a committed run never changes, so it is kept as long as the grid. */
export function priceRunQuery(runId: number) {
  return { queryKey: [...PRICE_RUN_KEY, runId] as const, queryFn: () => getPriceRun(runId), retry: 1, staleTime: GRID_STALE_TIME };
}

// Weekly margin series (C2 v1.5, release R5). People only, the area view. The shape lives in margin_weeks.ts.
export const getMarginWeeks = <T,>(market: Market, weeks: number, grain: "default" | "all") =>
  call<T>(`/margin-weeks/${market}?weeks=${weeks}${grain === "all" ? "&grain=all" : ""}`);
